import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { format, parseISO } from 'date-fns'

const empty = { club_name: '', league: '', country: '', start_date: '', end_date: '', appearances: 0, goals: 0, assists: 0, notes: '' }

export default function Career({ session }) {
  const uid = session.user.id
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState(empty)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => { load() }, [])

  const load = async () => {
    const { data } = await supabase.from('career_history').select('*').eq('user_id', uid).order('start_date', { ascending: false })
    setHistory(data || [])
    setLoading(false)
  }

  const save = async () => {
    setSaving(true)
    const payload = { ...form, user_id: uid, appearances: Number(form.appearances), goals: Number(form.goals), assists: Number(form.assists) }
    if (editing) {
      await supabase.from('career_history').update(payload).eq('id', editing)
    } else {
      await supabase.from('career_history').insert(payload)
    }
    await load()
    setSaving(false)
    setSaved(true)
    setTimeout(() => {
      setShowModal(false)
      setEditing(null)
      setForm(empty)
      setSaved(false)
    }, 700)
  }

  const del = async (id) => {
    if (!window.confirm('Delete this entry?')) return
    await supabase.from('career_history').delete().eq('id', id)
    setHistory(prev => prev.filter(h => h.id !== id))
  }

  const openEdit = (h) => {
    setForm(h)
    setEditing(h.id)
    setShowModal(true)
  }

  const totals = {
    clubs: history.length,
    apps: history.reduce((a, h) => a + (h.appearances || 0), 0),
    goals: history.reduce((a, h) => a + (h.goals || 0), 0),
    assists: history.reduce((a, h) => a + (h.assists || 0), 0),
  }

  if (loading) return <div style={{ color: 'var(--muted)' }}>Loading...</div>

  return (
    <div>
      <div className="flex-between mb-32">
        <div>
          <div className="page-title">Career History</div>
          <div className="page-sub">Every club you've played for</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm(empty); setEditing(null); setShowModal(true) }}>+ Add Club</button>
      </div>

      {/* Career totals */}
      <div className="grid-4 mb-32">
        {[
          { value: totals.clubs, label: 'Clubs' },
          { value: totals.apps, label: 'Total Apps' },
          { value: totals.goals, label: 'Total Goals' },
          { value: totals.assists, label: 'Total Assists' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Timeline */}
      {history.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <div className="empty-state-title">No career history yet</div>
            <div className="empty-state-body">Add every club you've played for to build your full career timeline.</div>
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>Add First Club</button>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-title">Timeline</div>
          <div style={{ position: 'relative' }}>
            {history.map((h, i) => (
              <div key={h.id} style={{ display: 'flex', gap: 20, paddingBottom: 32, position: 'relative' }}>
                {/* Timeline line */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--gold)', flexShrink: 0, marginTop: 4 }} />
                  {i < history.length - 1 && <div style={{ width: 1, flex: 1, background: 'var(--border)', marginTop: 4 }} />}
                </div>
                {/* Content */}
                <div style={{ flex: 1, background: 'var(--dark3)', border: '1px solid var(--border)', borderRadius: 6, padding: '20px 24px' }}>
                  <div className="flex-between mb-8">
                    <div>
                      <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 2 }}>{h.club_name}</div>
                      <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                        {h.league && `${h.league} · `}{h.country}
                        {h.start_date && ` · ${format(parseISO(h.start_date), 'MMM yyyy')} — ${h.end_date ? format(parseISO(h.end_date), 'MMM yyyy') : 'Present'}`}
                      </div>
                    </div>
                    <div className="flex gap-8">
                      <button onClick={() => openEdit(h)} className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }}>Edit</button>
                      <button onClick={() => del(h.id)} className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 12 }}>Delete</button>
                    </div>
                  </div>
                  <div className="flex gap-24" style={{ flexWrap: 'wrap' }}>
                    {h.appearances > 0 && <div><span style={{ fontFamily: 'Bebas Neue', fontSize: 22, color: 'var(--gold)', letterSpacing: 1 }}>{h.appearances}</span><span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 4 }}>Apps</span></div>}
                    {h.goals > 0 && <div><span style={{ fontFamily: 'Bebas Neue', fontSize: 22, color: 'var(--gold)', letterSpacing: 1 }}>{h.goals}</span><span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 4 }}>Goals</span></div>}
                    {h.assists > 0 && <div><span style={{ fontFamily: 'Bebas Neue', fontSize: 22, color: 'var(--gold)', letterSpacing: 1 }}>{h.assists}</span><span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 4 }}>Assists</span></div>}
                  </div>
                  {h.notes && <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 10, fontStyle: 'italic' }}>{h.notes}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-title">{editing ? 'Edit Club' : 'Add Club'}</div>
            <div className="grid-2 mb-16">
              <div><label>Club Name</label><input value={form.club_name || ''} onChange={e => setForm(p => ({...p, club_name: e.target.value}))} placeholder="FC Example" /></div>
              <div><label>League</label><input value={form.league || ''} onChange={e => setForm(p => ({...p, league: e.target.value}))} placeholder="Liga X" /></div>
            </div>
            <div className="grid-2 mb-16">
              <div><label>Country</label><input value={form.country || ''} onChange={e => setForm(p => ({...p, country: e.target.value}))} placeholder="Poland" /></div>
              <div></div>
            </div>
            <div className="grid-2 mb-16">
              <div><label>Start Date</label><input type="date" value={form.start_date || ''} onChange={e => setForm(p => ({...p, start_date: e.target.value}))} /></div>
              <div><label>End Date</label><input type="date" value={form.end_date || ''} onChange={e => setForm(p => ({...p, end_date: e.target.value}))} /></div>
            </div>
            <div className="grid-3 mb-16">
              <div><label>Appearances</label><input type="number" value={form.appearances || 0} onChange={e => setForm(p => ({...p, appearances: e.target.value}))} min={0} /></div>
              <div><label>Goals</label><input type="number" value={form.goals || 0} onChange={e => setForm(p => ({...p, goals: e.target.value}))} min={0} /></div>
              <div><label>Assists</label><input type="number" value={form.assists || 0} onChange={e => setForm(p => ({...p, assists: e.target.value}))} min={0} /></div>
            </div>
            <div className="mb-24"><label>Notes</label><textarea value={form.notes || ''} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2} placeholder="Any notes about this spell..." /></div>
            <div className="flex gap-8">
              <button className="btn btn-primary" onClick={save} disabled={saving || saved}>{saving ? <><span className="btn-spinner" /> Saving...</> : saved ? '✓ Saved' : 'Save'}</button>
              <button className="btn btn-ghost" onClick={() => { setShowModal(false); setEditing(null); setForm(empty) }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
